import React, { useState, useEffect } from 'react';
import Loader from '../Loader';
import OrgDashboard from '../common/OrgDashboard/OrgDashboard';
import useQuery from '../../hooks/useQueryParams';
import CustomMultiSelect from '../CustomMultiSelect';
import OrganizationSummary from './OrganizationSummary';
import { FiletypeCsv } from 'react-bootstrap-icons';
import { makeRESTApiRequest } from '../../helpers/api';
import { ADMIN_ROLE, ENDPOINTS, PROCESSOR_ROLE, SALES_PERSON_ROLE } from '../../helpers/constants';
import { Button } from 'react-bootstrap';
import { useAdvancedDateRangePicker } from '../../hooks/useAdvancedDateRangePicker';
import AdvanceDateRangePicker from '../common/AdvanceDateRangePicker';
import { individualOrdersOption, getUserFromLocalstrorage, isSalesPerson } from '../../helpers/global';
import CountySummary from './CountySummary';
import { AlertTriangleIcon } from 'lucide-react';
import TeamManagementStats from './TeamManagementStats';
import OrderStatusPipeline from './OrderStatusPipeline';

export default function Dashboard({ currentUserInfo }) {
  const { organizationId } = useQuery();
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [salesOrganizations, setSalesOrganizations] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const { dateRange, setDateRange, handleNavigateDate } = useAdvancedDateRangePicker();
  const isProcessorDashboard = currentUserInfo?.role === PROCESSOR_ROLE;

  const userData = currentUserInfo?.organization ? currentUserInfo : getUserFromLocalstrorage();

  useEffect(() => {
    if (userData?.organization && userData.organization.length > 0) {
      const orgOptions = userData.organization
        .map(org => ({
          value: org._id,
          label: org.name,
          accurateID: org.accurateID || null,
          isEnabled: org.isEnabled
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      setOrganizations(currentUserInfo.role === SALES_PERSON_ROLE ? orgOptions : [individualOrdersOption, ...orgOptions]);

      if (currentUserInfo.role === SALES_PERSON_ROLE) {
        const selectedOrgs = orgOptions.map(org => org.value);
        setSalesOrganizations(selectedOrgs);
      }

      // Set initial selection based on organizationId from URL
      if (organizationId) {
        const selectedOrg = orgOptions.find(org => org.value === organizationId);
        if (selectedOrg) {
          setSelectedOrganizations([selectedOrg.value]);
        }
      }
      // Don't select any organizations by default - let user choose
    }
  }, [userData?.organization, organizationId]);

  const handleOrganizationChange = selectedValues => {
    const selectedIds = selectedValues.map(item => item.value);
    setSelectedOrganizations(selectedIds);
  };

  const handleDownloadCSV = async () => {
    setIsDownloading(true);
    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.ORDER_EXPORT,
        method: 'POST',
        requestBody: {
          organizationIds: selectedOrganizations || []
        }
      });

      if (error) {
        console.error('Error downloading CSV:', error);
        return;
      }

      // Handle the new response structure
      let csvData = null;
      let filename = null;

      if (response && response.success && response.data) {
        csvData = response.data.csv;
        filename = response.data.filename;
      } else {
        console.error('Invalid response structure:', response);
        return;
      }

      if (csvData) {
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');

        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', filename || `orders-export-${new Date().toISOString().split('T')[0]}.csv`);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);

          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } else {
          console.error('Download not supported in this browser');
        }
      } else {
        console.error('No valid data received from API');
      }
    } catch (error) {
      console.error('Error downloading CSV:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isSalesPerson(currentUserInfo.role) && currentUserInfo.organization.length === 0) {
    return (
      <div className="w-100 py-5 px-5">
        <div
          className="d-flex flex-column justify-content-center align-items-center"
          style={{
            minHeight: '400px',
            border: '2px dashed #adb5bd',
            borderRadius: '8px',
            backgroundColor: '#ffffff',
            padding: '3rem'
          }}
        >
          <AlertTriangleIcon size={40} className="text-warning mb-3" />
          <h4 className="text-center text-muted fw-bold" style={{ margin: 0 }}>
            You are not assigned to any organizations yet. <br />
            Please contact your administrator to get assigned to an organization.
          </h4>
        </div>
      </div>
    );
  }

  if (isProcessorDashboard) {
    return (
      <div className="w-100" style={{ background: '#f8fafc', minHeight: 'calc(100vh - 64px)' }}>
        <div className="w-100 px-3 px-md-3 px-lg-4 pt-4">
          <div className="mb-4 d-flex flex-column flex-md-row align-items-md-center justify-content-between" style={{ gap: 12 }}>
            <span className="h3 fw-bold mb-0" style={{ letterSpacing: '-0.025em' }}>
              Processor Dashboard
            </span>
            <div style={{ width: '100%', maxWidth: '420px' }}>
              <CustomMultiSelect
                id="processor-organization-selector"
                onChange={handleOrganizationChange}
                selectedOptions={selectedOrganizations}
                options={organizations}
                placeholder="Select organizations..."
                showSelectAll={false}
                maxToShow={2}
                className="px-0 dashboard_multi_select_custom_style"
              />
            </div>
          </div>
          <TeamManagementStats />
          <OrderStatusPipeline organizationIds={selectedOrganizations} />
        </div>
      </div>
    );
  }

  return (
    <div className="w-100" style={{ background: '#f8fafc' }}>
      <OrgDashboard organizationIds={selectedOrganizations.length > 0 ? selectedOrganizations : currentUserInfo.role === SALES_PERSON_ROLE ? salesOrganizations : []} globalDateRange={dateRange}>
        <div
          className="d-flex flex-1 justify-content-between align-items-start w-100 flex-md-row flex-column-reverse align-items-md-end"
          style={{ gap: 4 }}
        >
          <div className="bg-white mr-2 rounded-[10px] h-100 download-button">
            <Button
              onClick={handleDownloadCSV}
              disabled={isDownloading}
              variant={'outline-success'}
              className="d-flex align-items-center h-100 py-2"
            >
              <FiletypeCsv size={16} style={{ marginRight: '6px' }} />
              <h6 className="mb-0 small" style={{ whiteSpace: 'nowrap' }}>
                {isDownloading ? 'Downloading...' : 'Download CSV'}
              </h6>
            </Button>
          </div>
          <AdvanceDateRangePicker
            value={dateRange}
            onChange={ranges => setDateRange(ranges.selection)}
            showNavigator={true}
            rangeType={dateRange.rangeType}
            handleNavigateDate={handleNavigateDate}
          />

          <div style={{ flexGrow: 1, minWidth: '330px' }}>
            <CustomMultiSelect
              id="organization-selector"
              onChange={handleOrganizationChange}
              selectedOptions={selectedOrganizations}
              options={organizations}
              placeholder="Select organizations..."
              showSelectAll={false}
              maxToShow={2}
              className="px-0 dashboard_multi_select_custom_style"
            />
          </div>
        </div>
      </OrgDashboard>

      <div
        className="w-100 px-3 px-md-3 px-lg-4 py-2"
        style={{ minHeight: 'calc(100vh - 64px)', background: '#f8fafc' }}
      >
        <div className="mb-4">
          <h4 className="fw-bold mb-3" style={{ color: '#212529', letterSpacing: '-0.025em' }}>
            Client Metrics
          </h4>
          <OrganizationSummary
            title="Organization Summary"
            headerBg="#e3f2fd"
            iconBg="#2196f3"
            globalDateRange={dateRange}
          />
        </div>
        {currentUserInfo.role === ADMIN_ROLE && (
          <div className="mb-4">
            <h4 className="fw-bold mb-3" style={{ color: '#212529', letterSpacing: '-0.025em' }}>
              Municipality Metrics
            </h4>
            <CountySummary title="County Summary" headerBg="#c7efb9" iconBg="#45bd19" globalDateRange={dateRange} />
          </div>
        )}
      </div>
    </div>
  );
}
